import { useEffect, useState } from 'react';
import * as api from '../../api';
import { Spinner, Empty, Modal, ErrorMessage } from '../../components';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
    subjectName: '', topicName: '', questionText: '',
    options: ['', '', '', ''], correctAnswer: '', label: 'basic'
};

const labelText = { basic: 'Oson', intermediate: 'O‘rta', advanced: 'Qiyin' };
const statusText = { pending: 'Tekshiruvda', approved: 'Tasdiqlangan', rejected: 'Rad etilgan' };

export default function AdminQuizzes() {
    const toast = useToast();
    const { user } = useAuth();
    const isAdmin = ['admin', 'superadmin'].includes(user?.role);
    const canManage = ['admin', 'superadmin', 'setter', 'editor'].includes(user?.role);
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({ subject: '', topic: '', label: '', status: '', search: '' });
    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState(1);
    const PER_PAGE = 15;

    const loadQuizzes = async () => {
        setLoading(true); setError('');
        try {
            const params = {};
            ['subject', 'topic', 'label', 'status'].forEach(k => { if (filters[k]) params[k] = filters[k]; });
            const res = await api.getQuizzes(params);
            setQuizzes(res.data || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { setPage(1); }, [filters]);
    useEffect(() => { loadQuizzes(); }, [filters.subject, filters.topic, filters.label, filters.status]);
    useEffect(() => { api.getSubjects().then(r => setSubjects(r.data)).catch(() => {}); }, []);
    useEffect(() => { api.getTopics(filters.subject).then(r => setTopics(r.data)).catch(() => setTopics([])); }, [filters.subject]);

    const displayed = filters.search
        ? quizzes.filter(q => [q.questionText, q.subjectName, q.topicName].some(v => String(v || '').toLowerCase().includes(filters.search.toLowerCase())))
        : quizzes;
    const paginated = displayed.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const totalPages = Math.ceil(displayed.length / PER_PAGE);

    const openCreate = () => { setForm(EMPTY_FORM); setModal('create'); };
    const openEdit = (q) => { setForm({ subjectName: q.subjectName, topicName: q.topicName, questionText: q.questionText, options: [...q.options], correctAnswer: q.correctAnswer, label: q.label }); setModal(q); };
    const setOpt = (i, v) => setForm(f => ({ ...f, options: f.options.map((x, idx) => idx === i ? v : x) }));

    const save = async () => {
        setSaving(true);
        try {
            if (modal === 'create') {
                const res = await api.createQuiz(form);
                toast(res.message || (isAdmin ? 'Savol qo‘shildi' : 'Savol yuborildi'));
            } else {
                const res = await api.updateQuiz(modal._id, form);
                toast(res.message || 'Savol yangilandi');
            }
            setModal(null); await loadQuizzes();
        } catch (e) { toast(e.message, 'error'); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm('Bu savolni o‘chirasizmi?')) return;
        try { await api.deleteQuiz(id); toast('Savol o‘chirildi'); await loadQuizzes(); }
        catch (e) { toast(e.message, 'error'); }
    };

    const review = async (q, action) => {
        try {
            const fn = action === 'approve' ? api.approveQuiz : api.rejectQuiz;
            await fn(q._id);
            toast(action === 'approve' ? 'Savol tasdiqlandi' : 'Savol rad etildi');
            await loadQuizzes();
        } catch (e) { toast(e.message, 'error'); }
    };

    const handleCSV = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        try { const res = await api.uploadQuizCSV(await file.text()); toast(res.message); await loadQuizzes(); }
        catch (err) { toast(err.message, 'error'); }
        e.target.value = '';
    };

    const downloadTemplate = () => {
        const csv = 'subject,topic,question,option1,option2,option3,option4,correct,label\nAnatomiya,Bosh suyagi,Bosh suyagining nechta asosiy qismi bor?,1,2,3,4,2,basic';
        const a = document.createElement('a'); a.href = 'data:text/csv,' + encodeURIComponent(csv); a.download = 'uniquiz_savollar.csv'; a.click();
    };

    if (!canManage) return <div className="page"><Empty title="Ruxsat yo‘q" message="Savollar bo‘limi faqat administrator, o‘qituvchi va savol muharrirlari uchun." /></div>;

    return (
        <div className="page">
            <div className="page-header">
                <div><h1>Savollar banki</h1><span style={{ color: 'var(--muted)', fontSize: 13 }}>{displayed.length} ta savol</span></div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={openCreate}>+ Savol qo‘shish</button>
                    {isAdmin && <><button className="btn btn-ghost" onClick={downloadTemplate}>⬇ Namuna</button><label className="btn btn-ghost" style={{ cursor: 'pointer' }}>⬆ CSV yuklash<input type="file" accept=".csv" onChange={handleCSV} style={{ display: 'none' }} /></label></>}
                </div>
            </div>

            <div className="filter-bar">
                <input placeholder="Savol qidirish…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} style={{ minWidth: 180 }} />
                <select value={filters.subject} onChange={e => setFilters(f => ({ ...f, subject: e.target.value, topic: '' }))}><option value="">Barcha fanlar</option>{subjects.map(s => <option key={s} value={s}>{s}</option>)}</select>
                <select value={filters.topic} onChange={e => setFilters(f => ({ ...f, topic: e.target.value }))}><option value="">Barcha mavzular</option>{topics.map(t => <option key={t} value={t}>{t}</option>)}</select>
                <select value={filters.label} onChange={e => setFilters(f => ({ ...f, label: e.target.value }))}><option value="">Barcha darajalar</option><option value="basic">Oson</option><option value="intermediate">O‘rta</option><option value="advanced">Qiyin</option></select>
                {isAdmin && <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}><option value="">Barcha holatlar</option><option value="pending">Tekshiruvda</option><option value="approved">Tasdiqlangan</option><option value="rejected">Rad etilgan</option></select>}
                {(filters.search || filters.subject || filters.topic || filters.label || filters.status) && <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ subject: '', topic: '', label: '', status: '', search: '' })}>Tozalash</button>}
            </div>

            <ErrorMessage message={error} />
            <div className="card" style={{ padding: 0 }}>
                {loading ? <Spinner /> : !paginated.length ? <Empty title="Savol topilmadi" message="Filtrlarni o‘zgartiring yoki yangi savol qo‘shing." /> : (
                    <div className="table-wrap">
                        <table>
                            <thead><tr><th>Fan</th><th>Mavzu</th><th>Savol</th><th>Daraja</th><th>Holat</th><th>Amallar</th></tr></thead>
                            <tbody>{paginated.map(q => (
                                <tr key={q._id}>
                                    <td>{q.subjectName}</td><td>{q.topicName}</td><td style={{ maxWidth: 320 }}>{q.questionText.length > 70 ? q.questionText.slice(0, 70) + '…' : q.questionText}</td>
                                    <td><span className={`badge badge-${q.label}`}>{labelText[q.label] || q.label}</span></td>
                                    <td><span className={`badge badge-${q.status}`}>{statusText[q.status] || q.status || 'Tasdiqlangan'}</span></td>
                                    <td><div className="td-actions">
                                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(q)}>Tahrirlash</button>
                                        <button className="btn btn-danger btn-sm" onClick={() => remove(q._id)}>O‘chirish</button>
                                        {isAdmin && q.status === 'pending' && <><button className="btn btn-primary btn-sm" onClick={() => review(q, 'approve')}>Tasdiqlash</button><button className="btn btn-ghost btn-sm" onClick={() => review(q, 'reject')}>Rad</button></>}
                                    </div></td>
                                </tr>
                            ))}</tbody>
                        </table>
                        {totalPages > 1 && <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 16 }}><button className="btn btn-ghost btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>← Oldingi</button><span style={{ color: 'var(--muted)', alignSelf: 'center' }}>{page} / {totalPages}</span><button className="btn btn-ghost btn-sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Keyingi →</button></div>}
                    </div>
                )}
            </div>

            {modal && <Modal title={modal === 'create' ? 'Savol qo‘shish' : 'Savolni tahrirlash'} onClose={() => setModal(null)} footer={<><button className="btn btn-ghost" onClick={() => setModal(null)}>Bekor qilish</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? <span className="spinner" /> : 'Saqlash'}</button></>}>
                <div className="form-row"><div className="form-group"><label>Fan</label><input value={form.subjectName} onChange={e => setForm(f => ({ ...f, subjectName: e.target.value }))} placeholder="Anatomiya" /></div><div className="form-group"><label>Mavzu</label><input value={form.topicName} onChange={e => setForm(f => ({ ...f, topicName: e.target.value }))} placeholder="Yurak" /></div></div>
                <div className="form-group"><label>Savol</label><textarea rows={3} value={form.questionText} onChange={e => setForm(f => ({ ...f, questionText: e.target.value }))} /></div>
                {form.options.map((o, i) => <div className="form-group" key={i}><label>{String.fromCharCode(65 + i)}-variant</label><input value={o} onChange={e => setOpt(i, e.target.value)} /></div>)}
                <div className="form-row"><div className="form-group"><label>To‘g‘ri javob</label><input value={form.correctAnswer} onChange={e => setForm(f => ({ ...f, correctAnswer: e.target.value }))} placeholder="Variant matni" /></div><div className="form-group"><label>Daraja</label><select value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}><option value="basic">Oson</option><option value="intermediate">O‘rta</option><option value="advanced">Qiyin</option></select></div></div>
                {!isAdmin && modal === 'create' && <div className="alert alert-success">Savol yuborilgach, administrator tasdiqlaydi.</div>}
            </Modal>}
        </div>
    );
}
